/**
 * MisEntradasPage — consulta pública de tus entradas de pagafacil.eu.
 *
 * Ruta: /mis-entradas
 *
 * El usuario introduce su email (y opcionalmente la referencia del pedido).
 * Devolvemos los pedidos vinculados a ese email — solo pagados / usados.
 *
 * Sin login: cualquiera con el email puede consultar. Es un patrón
 * aceptado en ticketera pequeña porque el QR y la validación físicamente
 * requieren el email real del comprador.
 */
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import Footer from '../components/Footer';

interface OrderTicket {
  id: string;
  order_reference: string;
  buyer_name: string;
  buyer_email: string;
  quantity: number;
  tickets_validated: number;
  status: string;
  show_id?: string | null;
  show_title?: string | null;
  show_slug?: string | null;
  show_datetime?: string | null;
  venue_name?: string | null;
  venue_city?: string | null;
}

export default function MisEntradasPage() {
  const [params] = useSearchParams();
  const initialRef = params.get('ref') || '';
  const [email, setEmail] = useState('');
  const [ref, setRef] = useState(initialRef);
  const [orders, setOrders] = useState<OrderTicket[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Mis entradas · Pagafácil';
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    setOrders(null);
    try {
      const r = await api.post<OrderTicket[]>('/public/entradas/lookup', {
        email,
        ref: ref || null,
      });
      setOrders(r.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'No se pudieron cargar las entradas');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ fontFamily: '"Hanken Grotesk", system-ui, sans-serif', background: '#F7F2E9', color: '#1E2A2E', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <header style={{ background: '#1E2A2E', color: '#F7F2E9', padding: '18px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to="/" style={{ color: 'inherit', textDecoration: 'none', fontFamily: '"Spectral", serif', fontSize: 22, fontWeight: 700 }}>pagafácil</Link>
        <Link to="/entradas" style={{ color: 'inherit', textDecoration: 'none', fontSize: 14, opacity: 0.85 }}>Ver cartelera</Link>
      </header>

      <section style={{ maxWidth: 720, margin: '0 auto', padding: '48px 24px 24px', width: '100%' }}>
        <div style={{ fontFamily: '"Space Mono", monospace', fontSize: 12, opacity: 0.6, letterSpacing: 2, textTransform: 'uppercase' }}>Consulta</div>
        <h1 style={{ fontFamily: '"Spectral", Georgia, serif', fontSize: 48, lineHeight: 1.05, margin: '10px 0 20px', fontWeight: 700 }}>
          Mis entradas
        </h1>
        <p style={{ opacity: 0.75, marginTop: 0, marginBottom: 32 }}>
          Introduce el email con el que compraste. Puedes añadir la referencia si quieres ver un pedido concreto.
        </p>

        <form onSubmit={submit} style={{ background: '#FFFFFF', border: '1px solid #EAE1D2', borderRadius: 16, padding: 24 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
            <span style={{ fontFamily: '"Space Mono", monospace', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.5, opacity: 0.7 }}>Email *</span>
            <input
              type="email" required
              value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@email.com"
              style={inputStyle}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
            <span style={{ fontFamily: '"Space Mono", monospace', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.5, opacity: 0.7 }}>Referencia (opcional)</span>
            <input
              value={ref} onChange={(e) => setRef(e.target.value)}
              placeholder="SH-1234567890-AB12"
              style={{ ...inputStyle, fontFamily: '"Space Mono", monospace' }}
            />
          </label>
          <button
            type="submit" disabled={busy || !email}
            style={{
              background: busy || !email ? '#B9B0A0' : '#1C5D82',
              color: '#FFFFFF',
              border: 'none',
              padding: '14px 22px',
              borderRadius: 12,
              fontWeight: 700,
              fontSize: 16,
              cursor: busy || !email ? 'not-allowed' : 'pointer',
              width: '100%',
            }}
          >
            {busy ? 'Buscando…' : 'Buscar entradas'}
          </button>
          {error && <div style={{ marginTop: 16, background: '#FDE7DE', color: '#8A2C22', padding: 12, borderRadius: 8, fontSize: 14 }}>{error}</div>}
        </form>
      </section>

      {orders !== null && (
        <section style={{ maxWidth: 720, margin: '0 auto', padding: '0 24px 60px', width: '100%' }}>
          {orders.length === 0 ? (
            <div style={{ background: '#FFFFFF', border: '1px solid #EAE1D2', borderRadius: 16, padding: 24, textAlign: 'center', opacity: 0.85 }}>
              No encontramos entradas para ese email.
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 16 }}>
              {orders.map((o) => <OrderCard key={o.id} order={o} />)}
            </div>
          )}
        </section>
      )}

      <div style={{ flex: 1 }} />
      <Footer />
    </div>
  );
}

function OrderCard({ order }: { order: OrderTicket }) {
  const remaining = order.quantity - order.tickets_validated;
  const state = order.status === 'used' ? 'Usada' : order.status === 'refunded' ? 'Reembolsada' : order.status === 'cancelled' ? 'Cancelada' : 'Vigente';
  const stateColor = order.status === 'used' ? '#8A8073' : order.status === 'refunded' ? '#B44528' : order.status === 'cancelled' ? '#B44528' : '#5E7B3E';

  return (
    <article style={{ background: '#1E2A2E', color: '#F7F2E9', borderRadius: 20, padding: 22 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <div style={{ fontFamily: '"Space Mono", monospace', fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: '#E8B44A' }}>Boarding pass</div>
        <div style={{ fontFamily: '"Space Mono", monospace', fontSize: 11, opacity: 0.6 }}>{order.order_reference}</div>
      </div>
      <div style={{ fontFamily: '"Spectral", serif', fontSize: 24, fontWeight: 700 }}>{order.show_title || 'Tu evento'}</div>
      {order.show_datetime && (
        <div style={{ fontSize: 14, opacity: 0.8, marginTop: 4 }}>
          {new Date(order.show_datetime).toLocaleString('es-ES', { dateStyle: 'full', timeStyle: 'short' })}
        </div>
      )}
      {(order.venue_name || order.venue_city) && (
        <div style={{ fontSize: 13, opacity: 0.7, marginTop: 2 }}>
          {order.venue_name || ''}{order.venue_city ? ` · ${order.venue_city}` : ''}
        </div>
      )}
      <div style={{ marginTop: 16, display: 'flex', gap: 16, justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div>
          <div style={{ fontSize: 11, opacity: 0.6, fontFamily: '"Space Mono", monospace', textTransform: 'uppercase', letterSpacing: 1.4 }}>Restan</div>
          <div style={{ fontFamily: '"Spectral", serif', fontSize: 24, fontWeight: 700 }}>{remaining} / {order.quantity}</div>
        </div>
        <div style={{ padding: '6px 12px', borderRadius: 10, background: stateColor, color: '#FFFFFF', fontSize: 12, fontWeight: 700, letterSpacing: 0.5 }}>
          {state}
        </div>
      </div>
      {order.show_slug && (
        <div style={{ marginTop: 16, borderTop: '1px solid rgba(247,242,233,0.15)', paddingTop: 12, fontSize: 13 }}>
          <Link to={`/e/${order.show_slug}`} style={{ color: '#E8B44A', textDecoration: 'none' }}>Ver el evento →</Link>
        </div>
      )}
    </article>
  );
}

const inputStyle: React.CSSProperties = {
  background: '#FBF7F0',
  border: '1px solid #EAE1D2',
  color: '#26201A',
  padding: '12px 14px',
  borderRadius: 8,
  fontSize: 16,
  fontFamily: 'inherit',
};
